import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { useCreateCategory, useUpdateCategory } from '@/utils/api/membership-service'
import type { MembershipCategoryRequest } from '@/utils/api/membership-service/models'
import type { MembershipCategory } from '@/utils/membership/types'
import { toast } from 'sonner'
import type { CategoryFormValues } from '../schema'
import { mapRequirementToApi } from '../../requirements/utils'
import { CategoryForm } from './CategoryForm'

interface CategoryDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  category?: MembershipCategory
  refetchAllCategories: () => void
}

export function CategoryDialog({ open, onOpenChange, category, refetchAllCategories }: CategoryDialogProps) {
  const isEditing = !!category

  const createMutation = useCreateCategory({
    mutation: {
      onSuccess: async () => {
        refetchAllCategories()
        toast.success('Category created successfully')
      },
      onError: (error: Error) => {
        toast.error(`Failed to create category: ${error.message}`)
      },
    },
  })

  const updateMutation = useUpdateCategory({
    mutation: {
      onSuccess: async () => {
        refetchAllCategories()
        toast.success('Category updated successfully')
      },
      onError: (error: Error) => {
        toast.error(`Failed to update category: ${error.message}`)
      },
    },
  })

  const handleSubmit = async (values: CategoryFormValues) => {
    try {
      const normalisedRequirements = values.requirements?.length
        ? values.requirements.map((requirement) => mapRequirementToApi(requirement))
        : undefined

      const payload: MembershipCategoryRequest = {
        ...values,
        requirements: normalisedRequirements,
      }

      if (isEditing) {
        await updateMutation.mutateAsync({
          id: category.id!,
          data: payload,
        })
      } else {
        await createMutation.mutateAsync({
          data: payload,
        })
      }
      onOpenChange(false)
    } catch (error) {
      // Error is handled by the mutation hooks
    }
  }

  const isSubmitting = createMutation.isPending || updateMutation.isPending

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[800px] max-h-[90vh] flex flex-col">
        <DialogHeader>
          <DialogTitle>{isEditing ? 'Edit Category' : 'Create New Category'}</DialogTitle>
          <DialogDescription>
            {isEditing
              ? 'Update the membership category details below.'
              : 'Add a new membership category for member registration.'}
          </DialogDescription>
        </DialogHeader>
        <CategoryForm
          defaultValues={category}
          onSubmit={handleSubmit}
          isSubmitting={isSubmitting}
          onCancel={() => onOpenChange(false)}
        />
      </DialogContent>
    </Dialog>
  )
}
